'use client';

import { CircleHalfIcon } from '@phosphor-icons/react';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer as Drawer } from '@/components/ui/responsive-drawer';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  PLATFORM_GROUPS,
  type PlatformFilter,
  type PlatformKey,
  platformLabel,
} from '@/lib/transactions/platform';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: PlatformFilter;
  onSelect: (value: PlatformFilter) => void;
  /** Facet counts from the meta endpoint (platformFacets). Facet-driven like
   *  PaymentMethodPickerDrawer's availableMethods: a channel with no transaction in the
   *  period is not offered — except the currently selected one, which must stay
   *  deselectable. */
  facetCounts: ReadonlyMap<string, number>;
}

/**
 * "Canal de compra" picker (VER-358). Same anatomy as PickerDrawer, plus the group split
 * PickerDrawer's flat list can't express: marketplaces vs the merchant-of-record
 * processors (dLocal, Paddle) — honest facts nobody recognizes as a store.
 *
 * There is deliberately no "compra direta" option: a null platform asserts no channel,
 * not "bought directly" (and it would match most rows anyway — Davi, 2026-08-15).
 */
export function PlatformPickerDrawer({ open, onOpenChange, value, onSelect, facetCounts }: Props) {
  const visibleGroups = PLATFORM_GROUPS
    .map((group) => ({
      label: group.label,
      keys: group.keys.filter((k) => facetCounts.has(k) || k === value),
    }))
    .filter((group) => group.keys.length > 0);

  const rowClass = (selected: boolean) => cn(
    'flex h-14 cursor-pointer items-center justify-between gap-3 border-b border-divider px-2 last:border-b-0',
    selected && 'text-foreground',
  );

  const optionRow = (key: PlatformKey) => {
    const selected = value === key;
    const count = facetCounts.get(key);
    return (
      <Label key={key} htmlFor={`platform-${key}`} className={rowClass(selected)}>
        <span className="flex min-w-0 items-center gap-3">
          <span className="truncate text-sm font-medium">{platformLabel(key)}</span>
        </span>
        <span className="flex shrink-0 items-center gap-3">
          {count !== undefined && (
            <span className="font-mono text-xs text-text-tertiary tabular-nums">{count}</span>
          )}
          <RadioGroupItem id={`platform-${key}`} value={key} />
        </span>
      </Label>
    );
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        data-tokens="v2"
        className="bg-surface px-4"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <DrawerHeader className="flex flex-row items-center justify-between px-0">
          <DrawerTitle className="text-lg font-semibold text-text-primary">
            Canal de compra
          </DrawerTitle>
          <DrawerDescription className="sr-only">
            O canal pelo qual a compra passou. Quem vendeu continua sendo o estabelecimento.
          </DrawerDescription>
          {value && (
            <button
              type="button"
              className="text-sm text-text-secondary hover:text-text-primary"
              onClick={() => {
                onSelect(null);
                onOpenChange(false);
              }}
            >
              Limpar
            </button>
          )}
        </DrawerHeader>

        <RadioGroup
          value={value ?? ''}
          onValueChange={(v) => {
            onSelect((v === '' ? null : v) as PlatformFilter);
            onOpenChange(false);
          }}
          className="gap-0"
        >
          <Label htmlFor="platform-all" className={rowClass(value === null)}>
            <span className="flex min-w-0 items-center gap-3">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-surface-sunken">
                <CircleHalfIcon className="size-4 text-text-primary" weight="duotone" />
              </span>
              <span className="truncate text-sm font-medium">Todas</span>
            </span>
            <RadioGroupItem id="platform-all" value="" />
          </Label>

          {visibleGroups.map((group) => (
            <div key={group.label}>
              <p className="px-2 pt-3 pb-1 text-2xs font-medium tracking-[0.15em] text-text-tertiary uppercase">
                {group.label}
              </p>
              {group.keys.map(optionRow)}
            </div>
          ))}
        </RadioGroup>
      </DrawerContent>
    </Drawer>
  );
}
